import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Demanda } from '../../../models'
import { useEventEmitter } from '../../../hooks/useEventBus'
import { receivedDemandsService } from '../../../services/receivedDemands'
import { toast } from '../../../components/Toast'
import { extractPlainText } from '../../../utils/formatter'
import { 
  useSolveDemandMutation, 
  useFinalizeDemandMutation,
  usePullDemandMutation,
  useAssignDemandMutation,
  useAcceptDemandMutation,
  useUndoDeliveryMutation,
  useReturnDemandMutation,
  useRejectDemandMutation,
  useMarkWrongDemandMutation,
  useRemandDemandMutation,
  useDeleteDemandMutation,
  useCommentDemandMutation,
  useRemoveAssignedMutation
} from './queries/useDemandMutations'
import { getErrorMsg } from '@/services'

interface DemandaExtended extends Demanda {
  demandados?: any[]
}

interface UseDemandActionsProps {
  demanda: DemandaExtended
  onSave: (updatedDemanda: Partial<DemandaExtended>) => void
  refetchHistorico: () => void
  showAlert: (
    type: 'info' | 'warning' | 'error' | 'success',
    message: string,
    title?: string
  ) => void
  showConfirm: (
    type: 'info' | 'warning' | 'danger' | 'success',
    message: string,
    onConfirm: () => void,
    details?: string,
    title?: string
  ) => void
  showPrompt: (
    message: string,
    onConfirm: (value: string) => void,
    options?: {
      title?: string
      placeholder?: string
      defaultValue?: string
      multiline?: boolean
      richText?: boolean
    }
  ) => void
  setSolveModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; loading?: boolean }>
  >
  setAssignModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>
  >
  setRemandModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>
  >
  setCommentAttachModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>
  >
  setRemoveAssignedModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>
  >
  setFinalizeModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>
  >
  setPromptModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading?: boolean; message: string }>
  >
}

interface RemandPayload {
  descricao?: string | undefined
  sugestao_entrega?: string | undefined
  template_respostas?: {
    template_id: string
    template_versao: string
    preenchido_em: string
    respostas: Record<string, unknown>
  } | undefined
  comentario?: string | undefined
}

export const useDemandActions = ({
  demanda,
  onSave,
  refetchHistorico,
  showAlert,
  showConfirm,
  showPrompt,
  setSolveModal,
  setAssignModal,
  setRemandModal,
  setCommentAttachModal,
  setRemoveAssignedModal,
  setFinalizeModal,
  setPromptModal,
}: UseDemandActionsProps) => {
  const { emit } = useEventEmitter();
  const navigate = useNavigate();
  
  // Mutations do TanStack Query
  const solveMutation = useSolveDemandMutation(demanda.id || 0);
  const finalizeMutation = useFinalizeDemandMutation(demanda.id || 0);
  const pullMutation = usePullDemandMutation(demanda.id || 0);
  const assignMutation = useAssignDemandMutation(demanda.id || 0);
  const acceptMutation = useAcceptDemandMutation(demanda.id || 0);
  const undoDeliveryMutation = useUndoDeliveryMutation(demanda.id || 0);
  const returnMutation = useReturnDemandMutation(demanda.id || 0);
  const rejectMutation = useRejectDemandMutation(demanda.id || 0);
  const markWrongMutation = useMarkWrongDemandMutation(demanda.id || 0);
  const remandMutation = useRemandDemandMutation(demanda.id || 0);
  const deleteMutation = useDeleteDemandMutation(demanda.id || 0);
  const commentMutation = useCommentDemandMutation(demanda.id || 0);
  const removeAssignedMutation = useRemoveAssignedMutation(demanda.id || 0);
  
  // Estados para atribuição de usuário
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [assignComment, setAssignComment] = useState('')

  // Estados para remoção de atribuídos
  const [usersToRemove, setUsersToRemove] = useState<number[]>([])

  // Função para lidar com a solução da demanda
  const handleSolveDemand = async (data: { comentario: string; solucionarTudo: boolean; nota: number }) => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    try {
      setSolveModal(prev => ({ ...prev, loading: true }))

      // Usar mutation do TanStack Query para atualização otimista
      await solveMutation.mutateAsync(data)

      // Emitir evento de mudança de status
      const newStatus = data.solucionarTudo ? 'SOLUCIONADA' : 'AGUARDANDOAUTORIZACAO'
      emit('demand-status-changed', {
        demandId: demanda.id!,
        oldStatus: demanda.status || '',
        newStatus: newStatus,
        demanda: { ...demanda, status: newStatus as Demanda['status'] }
      });
      
      // Fechar modal e mostrar toast
      setSolveModal({ isOpen: false, isMinimized: false, loading: false })

      const message = data.solucionarTudo
        ? 'Demanda solucionada por todos os responsáveis com sucesso!'
        : 'Demanda solucionada com sucesso!'
      toast.success(message)

      // Atualizar UI
      if (onSave && newStatus) {
        onSave({ status: newStatus })
      }

      // Invalidar caches relevantes
      receivedDemandsService.invalidateCache('received')
      receivedDemandsService.invalidateCache('toAssign')
      receivedDemandsService.invalidateCache('sent')

      // Recarregar histórico após solucionar
      refetchHistorico()
    } catch (error) {
      console.error('Erro ao solucionar demanda:', error)
      setSolveModal(prev => ({ ...prev, loading: false }))

      // const message = data.solucionarTudo
      //   ? 'Erro ao solucionar demanda por todos. Tente novamente.'
      //   : 'Erro ao solucionar demanda. Tente novamente.'
      showAlert('error', getErrorMsg(error), 'Erro')
    }
  }

  // Função para lidar com a atribuição de usuário
  const handleAssignUser = async () => {
    if (!demanda.id || !selectedUserId) {
      console.error('ID da demanda ou usuário não encontrado')
      showAlert('error', 'Dados necessários não encontrados', 'Erro')
      return
    }

    try {
      setAssignModal(prev => ({ ...prev, isLoading: true }))

      // Usar mutation para atualização otimista
      const comment = assignComment.trim();
      await assignMutation.mutateAsync({
        userId: selectedUserId,
        ...(comment && { comentario: comment })
      });

      // Emitir eventos
      emit('demand-assigned', {
        demandId: demanda.id!,
        assignedTo: [selectedUserId],
        demanda: { ...demanda, status: 'ATRIBUIDA' }
      });

      // Fechar modal, limpar estados e mostrar toast
      handleCloseAssignModal();
      toast.success('Demanda atribuída com sucesso!');

      // Atualizar UI
      if (onSave) {
        onSave({ status: 'ATRIBUIDA' })
      }

      // Recarregar histórico após atribuir
      refetchHistorico()
    } catch (error) {
      console.error('Erro ao atribuir demanda:', error)
      setAssignModal(prev => ({ ...prev, isLoading: false }))
      showAlert('error', getErrorMsg(error), 'Erro')
    }
  }

  // Função para fechar modal de atribuição e limpar estados
  const handleCloseAssignModal = () => {
    setSelectedUserId(null)
    setAssignComment('')
    setAssignModal({ isOpen: false, isMinimized: false, isLoading: false })
  }

  // Função para alternar seleção de usuário para remoção
  const handleToggleUserRemoval = (userId: number) => {
    setUsersToRemove(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  // Função para remoção em lote de atribuídos
  const handleBatchRemoveAssigned = async () => {
    if (usersToRemove.length === 0) {
      showAlert('warning', 'Selecione pelo menos um usuário para remover', 'Atenção')
      return
    }

    try {
      setRemoveAssignedModal(prev => ({ ...prev, isLoading: true }))

      // Remover cada usuário selecionado
      for (const userId of usersToRemove) {
        await removeAssignedMutation.mutateAsync(userId)
      }

      showAlert('success', `${usersToRemove.length} usuário(s) removido(s) com sucesso`, 'Sucesso')

      // Emitir eventos de atualização
      emit('demand-updated', {
        demandId: demanda.id!,
        changes: { atribuidas: 'removed' }
      })

      // Fechar modal e limpar seleção
      handleCloseRemoveAssignedModal()

      // Recarregar histórico
      refetchHistorico()
    } catch (error) {
      console.error('Erro ao remover atribuídos:', error)
      showAlert('error', getErrorMsg(error), 'Erro')
    } finally {
      setRemoveAssignedModal(prev => ({ ...prev, isLoading: false }))
    }
  }

  // Função para fechar modal de remoção e limpar estados
  const handleCloseRemoveAssignedModal = () => {
    setUsersToRemove([])
    setRemoveAssignedModal({ isOpen: false, isMinimized: false, isLoading: false })
  }

  // Função para lidar com o remandar de demanda
  const handleRemandDemand = async (data: RemandPayload): Promise<boolean> => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return false
    }

    try {
      setRemandModal(prev => ({ ...prev, isLoading: true }))

      await remandMutation.mutateAsync(data)

      // Construir objeto atualizado de forma segura
      const updatedFields: Partial<DemandaExtended> = {}
      if (data.descricao !== undefined) updatedFields.descricao = data.descricao
      if (data.sugestao_entrega !== undefined) updatedFields.sugestao_entrega = data.sugestao_entrega
      if (data.template_respostas !== undefined) updatedFields.template_respostas = data.template_respostas

      if (onSave && Object.keys(updatedFields).length > 0) {
        onSave(updatedFields)
      }

      toast.success('Demanda reenviada com sucesso!')

      // Recarregar histórico após remandar
      refetchHistorico()
      return true
    } catch (error) {
      console.error('Erro ao reenviar demanda:', error)
      showAlert('error', getErrorMsg(error), 'Erro')
      return false
    } finally {
      setRemandModal({ isOpen: false, isMinimized: false, isLoading: false })
    }
  }

  // Função para puxar demanda (assignToSelf)
  const handlePullToSelf = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showConfirm(
      'info',
      'Tem certeza que deseja puxar esta demanda para você?',
      async () => {
        try {
          // Usar mutation para atualização otimista
          await pullMutation.mutateAsync();
          
          // Emitir eventos
          emit('demand-status-changed', {
            demandId: demanda.id!,
            oldStatus: demanda.status || '',
            newStatus: 'EMPROGRESSO',
            demanda: { ...demanda, status: 'EMPROGRESSO' }
          });
          
          toast.success('Demanda atribuída para você com sucesso!');
          
          // Atualizar UI local
          if (onSave) {
            onSave({ status: 'EMPROGRESSO' });
          }
          
          refetchHistorico()
        } catch (error) {
          console.error('Erro ao puxar demanda:', error)
          showAlert('error', getErrorMsg(error), 'Erro')
        }
      },
      'Esta ação irá atribuir a demanda diretamente para você e mudará o status para "Em Progresso".',
      'Puxar Demanda'
    )
  }

  // Função para desfazer entrega
  const handleUndoDelivery = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Comentário sobre o motivo de desfazer a entrega (opcional):',
      async (comentario) => {
        try {
          // Ativar loading no modal
          setPromptModal(prev => ({ ...prev, isLoading: true }))

          // Usar mutation para atualização otimista
          const comment = comentario.trim();
          await undoDeliveryMutation.mutateAsync(comment || undefined);

          // Emitir evento de mudança de status
          emit('demand-status-changed', {
            demandId: demanda.id!,
            oldStatus: demanda.status || '',
            newStatus: 'EMPROGRESSO',
            demanda: { ...demanda, status: 'EMPROGRESSO' }
          });

          // Fechar modal e mostrar toast
          setPromptModal({ isOpen: false, isMinimized: false, isLoading: false, message: '' });
          toast.success('Entrega desfeita com sucesso!');

          if (onSave) {
            onSave({ status: 'EMPROGRESSO' })
          }
          refetchHistorico()
        } catch (error) {
          console.error('Erro ao desfazer entrega:', error)
          // Desativar loading em caso de erro
          setPromptModal(prev => ({ ...prev, isLoading: false }))
          showAlert('error', getErrorMsg(error), 'Erro')
        }
      },
      {
        title: 'Desfazer Entrega',
        placeholder: 'Digite o motivo para desfazer a entrega (opcional)',
        richText: true,
        defaultValue: '<p><strong>Motivo para desfazer entrega:</strong></p><p>• Identificada necessidade de ajustes adicionais</p><p>• Requisitos não foram completamente atendidos</p><p>• Solicitação de revisão do solicitante</p><p><br></p><p><em>Detalhes adicionais:</em></p>'
      }
    )
  }

  // Função para aceitar entrega (finalizar)
  const handleAcceptDelivery = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    // Abre o modal específico de finalização
    setFinalizeModal({ isOpen: true, isMinimized: false, isLoading: false })
  }

  // Função para confirmar a finalização da demanda (chamada pelo modal)
  const handleFinalizeDemand = async (data: { comentario: string; nota: number }) => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    try {
      setFinalizeModal(prev => ({ ...prev, isLoading: true }));
      
      // Usa a mutation para finalizar com optimistic update e nota
      await finalizeMutation.mutateAsync({
        ...(data.comentario && { comentario: data.comentario }),
        nota: data.nota
      });
      
      // Emitir evento de mudança de status
      emit('demand-status-changed', {
        demandId: demanda.id!,
        oldStatus: demanda.status || '',
        newStatus: 'FINALIZADA',
        demanda: { ...demanda, status: 'FINALIZADA' }
      });
      
      // Fecha o modal e mostra toast
      setFinalizeModal({ isOpen: false, isMinimized: false, isLoading: false });
      toast.success('Demanda finalizada com sucesso!');
      
      // Atualiza o status localmente
      const newStatus = 'FINALIZADA';
      if (onSave && newStatus) {
        onSave({ status: newStatus });
      }
      
      refetchHistorico();
    } catch (error) {
      console.error('Erro ao finalizar demanda:', error);
      setFinalizeModal(prev => ({ ...prev, isLoading: false }));
      showAlert('error', getErrorMsg(error), 'Erro');
    }
  }

  // Função para fechar modal de finalização e limpar estados
  const handleCloseFinalizeModal = () => {
    setFinalizeModal({ isOpen: false, isMinimized: false, isLoading: false })
  }

  // Função para devolver demanda
  const handleReturn = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Por que você está devolvendo esta demanda? (comentário obrigatório):',
      (comentario) => {
        if (!comentario.trim()) {
          showAlert('warning', 'É necessário informar o motivo da devolução.', 'Atenção')
          return
        }

        showConfirm(
          'warning',
          `Tem certeza que deseja devolver esta demanda?\n\nMotivo: ${extractPlainText(comentario)}\n\nEsta ação irá recusar a solução e retornar a demanda para nova atribuição.`,
          async () => {
            try {
              // Ativar loading no modal
              setPromptModal(prev => ({ ...prev, isLoading: true }))

              // Usar mutation para atualização otimista
              await returnMutation.mutateAsync(comentario);

              // Emitir evento de mudança de status
              emit('demand-status-changed', {
                demandId: demanda.id!,
                oldStatus: demanda.status || '',
                newStatus: 'DEVOLVIDA',
                demanda: { ...demanda, status: 'DEVOLVIDA' }
              });

              // Fechar modal e mostrar toast
              setPromptModal({ isOpen: false, isMinimized: false, isLoading: false, message: '' });
              toast.success('Demanda devolvida com sucesso! A solução foi recusada e a demanda retornará para nova atribuição.');

              if (onSave) {
                onSave({ status: 'EMATRIBUICAO' })
              }
              refetchHistorico()
            } catch (error) {
              console.error('Erro ao devolver demanda:', error)
              // Desativar loading em caso de erro
              setPromptModal(prev => ({ ...prev, isLoading: false }))
              showAlert('error', getErrorMsg(error), 'Erro')
            }
          },
          'Esta ação irá recusar a solução atual e devolver a demanda para nova atribuição.',
          'Devolver Demanda'
        )
      },
      {
        title: 'Devolver Demanda',
        placeholder: 'Digite o motivo da devolução (obrigatório)',
        richText: true,
        defaultValue: '<p><strong>Motivo da devolução:</strong></p><p>• A solução apresentada não atende aos requisitos</p><p>• Necessário esclarecimento adicional</p><p>• Requisitos não foram completamente implementados</p><p><br></p><p><em>Detalhes específicos:</em></p>'
      }
    )
  }

  // Função para aceitar demanda (muda status de RECEBIDA para EMPROGRESSO)
  const handleAccept = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Comentário sobre a aceitação da demanda (opcional):',
      async (comentario) => {
        try {
          // Usar mutation para atualização otimista
          const comment = comentario.trim();
          await acceptMutation.mutateAsync(comment || undefined);
          
          // Emitir evento de mudança de status
          emit('demand-status-changed', {
            demandId: demanda.id!,
            oldStatus: demanda.status || '',
            newStatus: 'EMPROGRESSO',
            demanda: { ...demanda, status: 'EMPROGRESSO' }
          });
          
          toast.success('Demanda aceita com sucesso! Agora você pode trabalhar nela.');
          
          if (onSave) onSave({ status: 'EMPROGRESSO' })
          refetchHistorico()
        } catch (error) {
          console.error('Erro ao aceitar demanda:', error)
          showAlert('error', 'Erro ao aceitar demanda. Tente novamente.', 'Erro')
        }
      },
      {
        title: 'Aceitar Demanda',
        placeholder: 'Digite um comentário sobre a aceitação (opcional)',
        richText: true,
        defaultValue: '<p><strong>Demanda aceita:</strong></p><p>• Compreendi os requisitos</p><p>• Vou iniciar o desenvolvimento</p><p><br></p><p><em>Observações:</em></p>'
      }
    )
  }

  // Função para recusar demanda
  const handleReject = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Por que você está recusando esta demanda? (comentário obrigatório):',
      (comentario) => {
        if (!comentario.trim()) {
          showAlert('warning', 'É necessário informar o motivo da recusa.', 'Atenção')
          return
        }
        
        showConfirm(
          'warning',
          `Tem certeza que deseja recusar esta demanda?\n\nMotivo: ${comentario}\n\nEsta ação irá recusar sua atribuição e pode retornar a demanda para nova atribuição se todos recusarem.`,
          async () => {
            try {
              // Usar mutation para atualização otimista
              await rejectMutation.mutateAsync(comentario);
              
              // Emitir eventos
              emit('demand-status-changed', {
                demandId: demanda.id!,
                oldStatus: demanda.status || '',
                newStatus: 'EMATRIBUICAO',
                demanda: { ...demanda, status: 'EMATRIBUICAO' }
              });
              
              toast.success('Demanda recusada com sucesso!');
              
              if (onSave) {
                onSave({ status: 'EMATRIBUICAO' })
              }
              refetchHistorico()
            } catch (error) {
              console.error('Erro ao recusar demanda:', error)
              showAlert('error', getErrorMsg(error), 'Erro')
            }
          },
          'Esta ação irá recusar sua atribuição à demanda.',
          'Recusar Demanda'
        )
      },
      {
        title: 'Recusar Demanda',
        placeholder: 'Digite o motivo da recusa (obrigatório)',
        multiline: true
      }
    )
  }

  // Função para excluir demanda
  const handleDelete = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showConfirm(
      'danger',
      'Tem certeza que deseja excluir esta demanda? Esta ação não pode ser desfeita.',
      async () => {
        try {
          // Usar mutation para deletar com atualização otimista
          await deleteMutation.mutateAsync();
          toast.success('Demanda excluída com sucesso!')

          // Emitir evento de atualização
          emit('demand-updated', {
            demandId: demanda.id!,
            changes: {
              type: 'deleted'
            }
          });

          toast.success('Demanda excluída com sucesso! Você será redirecionado para a lista de demandas.');

          // Redirecionar para a tela de demandas após um pequeno delay
          setTimeout(() => {
            navigate('/demands');
          }, 1500);

        } catch (error) {
          console.error('Erro ao excluir demanda:', error)
          showAlert('error', getErrorMsg(error), 'Erro')
        }
      },
      'Esta ação irá mudar o status da demanda para excluida e você será redirecionado para a lista de demandas.',
      'Excluir Demanda'
    )
  }

  // Função para marcar como errada
  const handleMarkWrong = async () => {
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Por que você está marcando esta demanda como errada? (comentário opcional):',
      async (comentario) => {
        const detailsMessage = [
          comentario ? `Motivo: ${extractPlainText(comentario)}` : ''
        ].filter(Boolean).join('\n\n');

        showConfirm(
          'warning',
          'Tem certeza que deseja marcar esta demanda como errada?',
          async () => {
            try {
              // Ativar loading no modal
              setPromptModal(prev => ({ ...prev, isLoading: true }))

              // Usar mutation para atualização otimista
              const comment = comentario.trim();
              await markWrongMutation.mutateAsync(comment || undefined);
                            
              // Emitir eventos
              emit('demand-status-changed', {
                demandId: demanda.id!,
                oldStatus: demanda.status || '',
                newStatus: 'ERRADA',
                demanda: { ...demanda, status: 'ERRADA' }
              });

              // Fechar modal e mostrar toast
              setPromptModal({ isOpen: false, isMinimized: false, isLoading: false, message: '' });
              toast.success('Demanda marcada como errada com sucesso! O demandante deve reenviar.');
              
              if (onSave) {
                onSave({ status: 'ERRADA' })
              }
              refetchHistorico()
            } catch (error) {
              console.error('Erro ao marcar demanda como errada:', error)
              // Desativar loading em caso de erro
              setPromptModal(prev => ({ ...prev, isLoading: false }))
              showAlert('error', getErrorMsg(error), 'Erro')
            }
          },
          `Esta ação fará com que o demandante precise reenviar a demanda.${detailsMessage ? `\n\n${detailsMessage}` : ''}`,
          'Marcar como Errada'
        )
      },
      {
        title: 'Marcar Demanda como Errada',
        placeholder: 'Digite o motivo (opcional)',
        multiline: true
      }
    )
  }

  // Função para comentar
  const handleComment = () => {
    
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    showPrompt(
      'Digite seu comentário sobre a demanda:',
      async (comentario) => {
        if (!comentario.trim()) {
          showAlert('warning', 'É necessário informar um comentário.', 'Atenção')
          return
        }
        
        try {
          // Ativar loading no modal
          setPromptModal(prev => ({ ...prev, isLoading: true }))
          
          // Usar mutation para comentar
          await commentMutation.mutateAsync(comentario);
                    
          // Emitir evento de comentário adicionado
          emit('demand-updated', {
            demandId: demanda.id!,
            changes: {
              type: 'comment-added',
              comment: comentario.trim()
            }
          });
          
          // Fechar modal
          setPromptModal({ isOpen: false, isMinimized: false, isLoading: false, message: '' })
          
          // Mostrar toast de sucesso
          toast.success('Comentário adicionado com sucesso!')
          
          // Recarregar histórico
          refetchHistorico()
        } catch (error) {
          console.error('Erro ao fazer comentário:', error)
          
          // Desativar loading
          setPromptModal(prev => ({ ...prev, isLoading: false }))
          
          showAlert('error', getErrorMsg(error), 'Erro')
        }
      },
      {
        title: 'Fazer Comentário',
        placeholder: 'Digite seu comentário sobre a demanda...',
        richText: true,
        defaultValue: ''
      }
    )
  }

  // Função para anexar (abre modal)
  const handleAttach = () => {
    
    if (!demanda.id) {
      console.error('ID da demanda não encontrado')
      showAlert('error', 'ID da demanda não encontrado', 'Erro')
      return
    }

    setCommentAttachModal({ isOpen: true, isMinimized: false, isLoading: false })
  }

  // Função para abrir modal de remoção de atribuídos
  const handleOpenRemoveAssignedModal = () => {
    setRemoveAssignedModal({ isOpen: true, isMinimized: false, isLoading: false })
  }

  return {
    // Estados de atribuição
    selectedUserId,
    setSelectedUserId,
    assignComment,
    setAssignComment,

    // Estados de remoção de atribuídos
    usersToRemove,
    setUsersToRemove,

    // Funções de ação
    handleSolveDemand,
    handleAssignUser,
    handleCloseAssignModal,
    handleToggleUserRemoval,
    handleBatchRemoveAssigned,
    handleCloseRemoveAssignedModal,
    handleRemandDemand,
    
    // Novas funções implementadas
    handlePullToSelf,
    handleUndoDelivery,
    handleAcceptDelivery,
    handleFinalizeDemand,
    handleCloseFinalizeModal,
    handleAccept,
    handleReturn,
    handleReject,
    handleDelete,
    handleMarkWrong,
    handleComment,
    handleAttach,
    handleOpenRemoveAssignedModal,
  }
}
